package dev.lingvo.site.pages.app.projects.slug.settings

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.minWidth
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.ProjectAccentKey
import dev.lingvo.shared.ProjectDetailDto
import dev.lingvo.shared.ProjectIconKey
import dev.lingvo.shared.ProjectKind
import dev.lingvo.shared.UpdateProjectRequest
import dev.lingvo.shared.localeFlagPrefix
import dev.lingvo.shared.localeInfo
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.ApiException
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCheckIcon
import dev.lingvo.site.ds.LProjectMark
import dev.lingvo.site.ds.elevation
import dev.lingvo.site.ds.LRefreshIcon
import dev.lingvo.site.ds.LSectionHeading
import dev.lingvo.site.ds.LSelect
import dev.lingvo.site.ds.LSelectOption
import dev.lingvo.site.ds.LTextArea
import dev.lingvo.site.ds.LTextField
import dev.lingvo.site.ds.LToastVariant
import dev.lingvo.site.ds.LWrenchIcon
import dev.lingvo.site.ds.Elevation
import dev.lingvo.site.ds.Radius
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TOAST_UNTIL_DISMISSED
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.tf
import dev.lingvo.site.pages.app.projects.slug.SettingsDraftProtection
import dev.lingvo.site.state.ProjectDirectory
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

internal data class ProjectIdentityDraft(
    val name: String,
    val description: String,
    val styleGuide: String,
    val sourceLocale: String,
    val kind: ProjectKind,
    val iconKey: ProjectIconKey,
    val accentKey: ProjectAccentKey,
) {
    fun isDirtyFrom(loaded: ProjectIdentityDraft): Boolean = this != loaded

    fun resetTo(loaded: ProjectIdentityDraft): ProjectIdentityDraft = loaded

    fun toRequest(): UpdateProjectRequest = UpdateProjectRequest(
        name = name.trim(),
        description = description.trim(),
        styleGuide = styleGuide.trim(),
        sourceLocale = sourceLocale,
        kind = kind,
        iconKey = iconKey,
        accentKey = accentKey,
    )

    companion object {
        fun from(project: ProjectDetailDto): ProjectIdentityDraft = ProjectIdentityDraft(
            name = project.name,
            description = project.description.orEmpty(),
            styleGuide = project.styleGuide.orEmpty(),
            sourceLocale = project.sourceLocale,
            kind = project.kind,
            iconKey = project.iconKey,
            accentKey = project.accentKey,
        )
    }
}

internal suspend fun persistProjectIdentity(
    slug: String,
    draft: ProjectIdentityDraft,
    updateProject: suspend (String, UpdateProjectRequest) -> ProjectDetailDto,
    invalidate: () -> Unit,
    replace: (ProjectDetailDto) -> Unit,
): ProjectDetailDto {
    val updated = updateProject(slug, draft.toRequest())
    invalidate()
    replace(updated)
    return updated
}

/**
 * Refetches the project after its icon changed, and says so when the refetch fails.
 *
 * **The failure used to be swallowed whole.** This was `runCatching { ApiClient.getProject(slug) }`
 * with an `onSuccess` arm and nothing else — forbidden around anything that suspends, and here it
 * hid the exact symptom the refetch exists to prevent: upload an icon, let the refetch fail (or be
 * cancelled), and the preview stays empty, the label still reads "Upload an icon", no Remove button
 * appears, and nothing on screen says why. [settingsOperationResult] rethrows cancellation — a
 * reader who switched tabs is not shown an error — and hands back every real failure to report.
 *
 * The bytes are already on the server when this runs, so the message says the icon was saved: a
 * plain "Couldn't save that icon" here would be a lie that costs somebody a second upload.
 */
internal suspend fun refreshProjectAfterIconChange(
    slug: String,
    fetchProject: suspend (String) -> ProjectDetailDto,
    invalidate: () -> Unit,
    replace: (ProjectDetailDto) -> Unit,
    onSaved: (ProjectDetailDto) -> Unit,
    notify: (String, LToastVariant, Long) -> Int,
) {
    invalidate()
    settingsOperationResult { fetchProject(slug) }
        .onSuccess { updated ->
            replace(updated)
            onSaved(updated)
        }
        .onFailure { failure ->
            notify(
                failure.iconRefreshMessage(),
                LToastVariant.DANGER,
                TOAST_UNTIL_DISMISSED,
            )
        }
}

/**
 * What the reader is told when the post-upload refetch fails.
 *
 * The server's own sentence when it sent one, and this page's otherwise — never a `check`/`require`
 * message written for a developer. Either way it says the icon *was* saved, because it was: the
 * bytes landed before this refetch ran, and "couldn't save that icon" would cost somebody a second
 * upload of a file that is already stored.
 */
private fun Throwable.iconRefreshMessage(): String =
    (this as? ApiException)?.message?.takeIf(String::isNotBlank)
        ?: tr(
            "app.projects.slug.settings.project_identity_settings.icon_saved_refresh_failed",
            "The icon was saved, but this page couldn't reload it. Refresh to see it.",
        )

@Composable
internal fun ProjectIdentitySettings(
    project: ProjectDetailDto,
    slug: String,
    onSaved: (ProjectDetailDto) -> Unit,
    notify: (String, LToastVariant, Long) -> Int,
    updateProject: suspend (String, UpdateProjectRequest) -> ProjectDetailDto = { projectSlug, request ->
        ApiClient.updateProject(projectSlug, request)
    },
    invalidate: () -> Unit = { ProjectDirectory.invalidate() },
    replace: (ProjectDetailDto) -> Unit = { ProjectDirectory.replace(it) },
    fetchProject: suspend (String) -> ProjectDetailDto = { projectSlug -> ApiClient.getProject(projectSlug) },
) {
    val scope = rememberCoroutineScope()
    var loaded by remember(project.updatedAt) { mutableStateOf(ProjectIdentityDraft.from(project)) }
    var draft by remember(project.updatedAt) { mutableStateOf(loaded) }
    var saving by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    val dirty = draft.isDirtyFrom(loaded)

    SettingsDraftProtection(
        dirty = dirty,
        guardId = "project-identity",
        onDiscard = {
            draft = draft.resetTo(loaded)
            error = null
        },
    )

    SettingsSection(
        title = t("app.projects.slug.settings.project_identity_settings.general", "General"),
        description = t("app.projects.slug.settings.project_identity_settings.project_identity_writing_context_and_the", "Project identity, writing context, and the source language translators work from."),
        icon = { LWrenchIcon() },
    ) {
        SettingsSubsection(
            title = t("app.projects.slug.settings.project_identity_settings.project_identity", "Project identity"),
            description = t("app.projects.slug.settings.project_identity_settings.the_mark_and_accent_identify_this", "The mark and accent identify this project wherever projects appear together."),
        ) {
            IdentityPreview(project.slug, draft)
            // Above the preset grid, because an uploaded mark supersedes whatever is chosen there —
            // offering the presets first and the override second reads as the wrong order.
            ProjectIconUpload(
                slug = project.slug,
                name = draft.name,
                iconKey = draft.iconKey,
                accentKey = draft.accentKey,
                hasCustomIcon = project.hasCustomIcon,
                // **The detail has to be refetched, and `invalidate` does not do that.**
                //
                // This was wired to `invalidate`, which clears the project *list* cache only. The
                // page's own `project` came from `loadAsync(slug, refreshKey)`, so `hasCustomIcon`
                // kept the value it had at page load: upload an icon and the preview stayed empty,
                // the label still read "Upload an icon", and no Remove button appeared. The
                // feature looked like it had failed while the bytes were sitting on the server.
                //
                // `onSaved` is the callback the page binds to `refreshKey++`. It wants the fresh
                // DTO, so this fetches it — one request, and every surface reading the project
                // follows.
                onChanged = {
                    scope.launch {
                        refreshProjectAfterIconChange(
                            slug = slug,
                            fetchProject = fetchProject,
                            invalidate = invalidate,
                            replace = replace,
                            onSaved = onSaved,
                            notify = notify,
                        )
                    }
                },
            )
            ProjectIdentityPicker(
                name = draft.name,
                iconKey = draft.iconKey,
                accentKey = draft.accentKey,
                onIconSelected = { draft = draft.copy(iconKey = it) },
                onAccentSelected = { draft = draft.copy(accentKey = it) },
            )
        }
        SettingsSubsection(
            title = t("app.projects.slug.settings.project_identity_settings.project_details", "Project details"),
            description = t("app.projects.slug.settings.project_identity_settings.give_translators_enough_context_to_recognise", "Give translators enough context to recognise the product and where its copy appears."),
        ) {
            Div(
                Modifier
                    .fillMaxWidth()
                    .gap(Space.MD)
                    .styleModifier {
                        property("display", "grid")
                        property("grid-template-columns", "repeat(auto-fit, minmax(min(100%, 232px), 1fr))")
                    }
                    .toAttrs(),
            ) {
                LTextField(
                    value = draft.name,
                    onValueChange = { draft = draft.copy(name = it) },
                    label = t("app.projects.slug.settings.project_identity_settings.project_name", "Project name"),
                    error = if (draft.name.isBlank()) tr("app.projects.slug.settings.project_identity_settings.enter_a_project_name", "Enter a project name") else null,
                    enabled = !saving,
                )
                LSelect(
                    value = draft.kind.name,
                    options = ProjectKind.entries.map { kind -> LSelectOption(kind.name, kind.displayLabel()) },
                    onValueChange = { draft = draft.copy(kind = ProjectKind.valueOf(it)) },
                    label = t("app.projects.slug.settings.project_identity_settings.project_kind", "Project kind"),
                    enabled = !saving,
                )
            }
            LTextArea(
                value = draft.description,
                onValueChange = { draft = draft.copy(description = it) },
                label = t("app.projects.slug.settings.project_identity_settings.description", "Description"),
                placeholder = t("app.projects.slug.settings.project_identity_settings.what_this_product_does_and_where", "What this product does and where its copy appears"),
                enabled = !saving,
            )
        }
        SettingsSubsection(
            title = t("app.projects.slug.settings.project_identity_settings.translation_context", "Translation context"),
            description = t("app.projects.slug.settings.project_identity_settings.set_the_writing_guidance_and_the", "Set the writing guidance and the language every translation starts from."),
        ) {
            LTextArea(
                value = draft.styleGuide,
                onValueChange = { draft = draft.copy(styleGuide = it) },
                label = t("app.projects.slug.settings.project_identity_settings.translation_style_guide", "Translation style guide"),
                placeholder = t("app.projects.slug.settings.project_identity_settings.address_the_user_directly_keep_button", "Address the user directly. Keep button labels under two words."),
                enabled = !saving,
            )
            LSelect(
                value = draft.sourceLocale,
                options = (listOf(loaded.sourceLocale) + project.locales).distinct().map { code ->
                    val info = localeInfo(code)
                    LSelectOption(code, "${localeFlagPrefix(info.code)}${info.englishName} — $code")  // i18n-ignore — a flag glyph immediately followed by a language name — no separator, no words, nothing a translator could change
                },
                onValueChange = { draft = draft.copy(sourceLocale = it) },
                label = t("app.projects.slug.settings.project_identity_settings.source_locale", "Source language"),
                enabled = !saving,
            )
            if (draft.sourceLocale != loaded.sourceLocale) {
                LAlert(
                    title = t("app.projects.slug.settings.project_identity_settings.this_changes_what_everyone_translates_from", "This changes what everyone translates from"),
                    // Two sentences with named holes rather than four concatenated fragments: a
                    // translator handed "Existing " and " text stays where it is" cannot put the
                    // language code where their own grammar needs it.
                    description = tf(
                        "app.projects.slug.settings.project_identity_settings.source_locale_change_consequence",
                        "Existing {current} text stays where it is and becomes a target language. " +
                            "Translators will see {next} as the original from now on.",
                        "current" to loaded.sourceLocale,
                        "next" to draft.sourceLocale,
                    ),
                    variant = LAlertVariant.WARNING,
                )
            }
        }
        error?.let {
            LAlert(t("app.projects.slug.settings.project_identity_settings.couldnt_save_project_settings", "Couldn't save project settings"), it, variant = LAlertVariant.DANGER, live = true)
        }
        // **Painted as a floating layer, because it floats.** `surface` + a hairline is the paint
        // of the cards this bar is sticky-positioned *over* — it spends most of the scroll covering
        // the accent picker, and it read as a card that had drifted out of the flow rather than as
        // a layer above one. `docs/DESIGN.md` reserves fill plus shadow for a floating layer;
        // `Elevation.Sheet` is the upward-thrown rung that exists for exactly this bottom-anchored
        // case, so the shadow lands on the content the bar is covering instead of off-screen.
        Div(
            Modifier
                .fillMaxWidth()
                .backgroundColor(ColorMode.current.toSitePalette().surfaceRaised)
                .border(1.px, LineStyle.Solid, ColorMode.current.toSitePalette().borderStrong)
                .borderRadius(Radius.Card)
                .elevation(Elevation.Sheet)
                .padding(Space.MD)
                .gap(Space.SM)
                .styleModifier {
                    property("position", "sticky")
                    property("bottom", Space.MD.toString())
                    property("z-index", "2")
                    property("display", "flex")
                    property("align-items", "center")
                    property("justify-content", "space-between")
                    property("flex-wrap", "wrap")
                }
                .attr("data-identity-footer", "")
                .toAttrs(),
        ) {
            Span(
                Modifier
                    .color(ColorMode.current.toSitePalette().textSecondary)
                    .fontSize(TypeScale.Small)
                    .styleModifier {
                        property("min-width", "0")
                        property("flex", "1 1 136px")
                    }
                    .toAttrs(),
            ) {
                Text(if (dirty) t("app.projects.slug.settings.project_identity_settings.unsaved_changes", "Unsaved changes") else t("app.projects.slug.settings.project_identity_settings.all_changes_saved", "All changes saved"))
            }
            Div(
                Modifier
                    .gap(Space.SM)
                    .styleModifier {
                        property("display", "flex")
                        property("align-items", "center")
                        property("justify-content", "flex-end")
                        property("flex", "1 1 auto")
                        property("flex-wrap", "wrap")
                        property("min-width", "0")
                    }
                    .attr("data-identity-actions", "")
                    .toAttrs(),
            ) {
                if (dirty) {
                    LButton(
                        label = t("app.projects.slug.settings.project_identity_settings.reset", "Reset"),
                        onClick = { draft = draft.resetTo(loaded); error = null },
                        variant = LButtonVariant.GHOST,
                        enabled = !saving,
                        leadingIcon = { LRefreshIcon() },
                    )
                }
                LButton(
                    label = t("app.projects.slug.settings.project_identity_settings.save_changes", "Save changes"),
                    onClick = {
                        scope.launch {
                            saving = true
                            error = null
                            try {
                                val updated = persistProjectIdentity(slug, draft, updateProject, invalidate, replace)
                                loaded = ProjectIdentityDraft.from(updated)
                                draft = loaded
                                onSaved(updated)
                                notify(tr("app.projects.slug.settings.project_identity_settings.project_settings_saved", "Project settings saved"), LToastVariant.SUCCESS, 4_000)
                            } catch (cancelled: CancellationException) {
                                throw cancelled
                            } catch (failure: Throwable) {
                                error = failure.message?.takeIf(String::isNotBlank)
                                    ?: tr("app.projects.slug.settings.project_identity_settings.check_your_connection", "Check your connection and retry.")
                            } finally {
                                saving = false
                            }
                        }
                    },
                    enabled = dirty && draft.name.isNotBlank() && !saving,
                    // **The accent means "this is the thing you act on".** Nothing is dirty on
                    // load, so this button is genuinely disabled — and a disabled accent fill is
                    // still the one emphatic control on a screen whose own status line reads "All
                    // changes saved". `disabledSurface()` drops the opacity to 0.56, which on an
                    // accent fill reads as a live primary at 2.8:1 rather than as an unavailable
                    // one. The button stays (the surface test asserts it exists and is disabled);
                    // it claims primacy only once there is something to save.
                    variant = if (dirty) LButtonVariant.PRIMARY else LButtonVariant.SECONDARY,
                    leadingIcon = { LCheckIcon() },
                    loading = saving,
                )
            }
        }
    }
}

@Composable
private fun IdentityPreview(slug: String, draft: ProjectIdentityDraft) {
    Row(
        Modifier
            .fillMaxWidth()
            .backgroundColor(ColorMode.current.toSitePalette().surfaceSunken)
            .border(1.px, LineStyle.Solid, ColorMode.current.toSitePalette().border)
            .borderRadius(Radius.Card)
            .padding(Space.LG)
            .gap(Space.MD)
            .attr("data-identity-preview", ""),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        LProjectMark(
            draft.name,
            draft.iconKey,
            draft.accentKey,
            Modifier.width(48.px).height(48.px),
        )
        Column(Modifier.minWidth(0.px).gap(Space.XS)) {
            Span(Modifier.fontSize(TypeScale.Body).fontWeight(700).toAttrs { attr("dir", "auto") }) {
                Text(draft.name.ifBlank { t("app.projects.slug.settings.project_identity_settings.untitled_project", "Untitled project") })
            }
            Span(
                Modifier.color(ColorMode.current.toSitePalette().textSecondary).fontSize(TypeScale.Small)
                    .toAttrs { attr("dir", "auto") },
            ) {
                Text(draft.description.ifBlank { t("app.projects.slug.settings.project_identity_settings.no_project_description_yet", "No project description yet") })
            }
            Span(
                Modifier.color(ColorMode.current.toSitePalette().textMuted).fontSize(TypeScale.Small).toAttrs(),
            ) {
                Text(tf(
                    "app.projects.slug.settings.project_identity_settings.slug_kind_source",
                    "{slug} · {kind} · {source}",
                    "slug" to slug,
                    "kind" to draft.kind.displayLabel(),
                    "source" to draft.sourceLocale,
                ))
            }
        }
    }
}

@Composable
private fun SettingsSubsection(
    title: String,
    description: String,
    content: @Composable () -> Unit,
) {
    Column(Modifier.fillMaxWidth().gap(Space.MD)) {
        Column(Modifier.gap(Space.XS)) {
            H3(Modifier.fontSize(TypeScale.Body).fontWeight(700).margin(0.px).toAttrs()) { Text(title) }
            P(
                Modifier.color(ColorMode.current.toSitePalette().textSecondary).fontSize(TypeScale.Small)
                    .margin(0.px).toAttrs(),
            ) { Text(description) }
        }
        content()
    }
}

/**
 * One settings section: its heading, its explanation, and the controls under them.
 *
 * **The heading is [LSectionHeading] now, not a hand-written `H2` + `P`.** This file had its own
 * copy of that pairing — same `HeadingSmall` at 700, same `textSecondary` body-small paragraph, same
 * zeroed margins, same `Space.XS` between them — which is exactly the duplication the design-system
 * component was extracted to end. Going through it is also what makes [icon] and [actions] possible
 * here at all: a glyph beside the title and the section's own controls at the inline end, laid out
 * the same way on every settings surface in the product rather than re-decided per file.
 */
@Composable
internal fun SettingsSection(
    title: String,
    description: String,
    wide: Boolean = false,
    icon: (@Composable () -> Unit)? = null,
    actions: (@Composable () -> Unit)? = null,
    content: @Composable () -> Unit,
) {
    Column(
        Modifier
            .fillMaxWidth()
            .maxWidth(if (wide) SettingsWideMeasure else SettingsFormMeasure)
            .gap(Space.LG),
    ) {
        LSectionHeading(
            title = title,
            description = description,
            modifier = Modifier.fillMaxWidth(),
            icon = icon,
            actions = actions,
        )
        content()
    }
}
